import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BarChart } from "./ChartTypes/Barchart";
import { PieChart } from "./ChartTypes/Piechart";
import { StackedBarChart } from "./ChartTypes/Pilechart";
import { exportRoot } from '../../index';
import domtoimage from 'dom-to-image-more';
import jsPDF from 'jspdf';

// Helper function to generate clean export filenames
const generateExportFilename = (title, chartType, year, country) => {
    if (!title) return 'ChartExport';

    // Clean the title for filename use
    let filename = title
        .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
        .replace(/\s+/g, '_') // Replace spaces with underscores
        .replace(/_+/g, '_') // Replace multiple underscores with single
        .replace(/^_|_$/g, ''); // Remove leading/trailing underscores

    // Add chart type and year if not already in title
    if (chartType && !filename.toLowerCase().includes(chartType.toLowerCase())) {
        filename += `_${chartType}`;
    }
    if (year && !filename.includes(year)) {
        filename += `_${year}`;
    }

    // Limit filename length
    if (filename.length > 100) {
        filename = filename.substring(0, 100);
    }

    return filename || 'ChartExport';
};

export const Chart = ({
    data,
    chartType,
    year,
    property,
    triggerExportPDF,
    country
}) => {
    const [chartData, setChartData] = useState([]);
    const [start, setStart] = useState(false);
    const [title, setTitle] = useState("");
    const [selectedTypology, setSelectedTypology] = useState("All");

    useEffect(() => {
        setStart(false);
        if (Array.isArray(data)) {
            setChartData(data);
        } else {
            setChartData([]);
        }
    }, [data]);

    useEffect(() => {
        setTimeout(() => {
            if (chartData.length) {
                const hasData = chartData.some(item => {
                    if (Array.isArray(item)) {
                        return item.some(inner => inner && Object.keys(inner).length > 0);
                    } else if (item && typeof item === "object") {
                        return Object.keys(item).length > 0;
                    }
                    return false;
                });
                setStart(hasData);
            } else {
                setStart(false);
            }
        }, 250);
    }, [chartData]);

    useEffect(() => {
        if (exportRoot) {
            exportRoot.render(
                <HiddenChartContent
                    data={data}
                    chartType={chartType}
                    year={year}
                    property={property}
                    country={country}
                    setTitle={setTitle}
                    selectedTypology={selectedTypology}
                />
            );
        }
    }, [data, chartType, year, property, country, selectedTypology]);

    const handleExportPDF = useCallback(() => {
        // Check browser compatibility
        if (!window.Blob || !window.URL || !document.createElement('canvas').getContext) {
            alert('PDF export is not supported in this browser. Please use a modern browser like Chrome, Firefox, or Safari.');
            return;
        }

        const container = document.getElementById('exportContainer');
        if (!container) {
            console.error('Export container not found');
            return;
        }

        // Wait for any pending renders
        setTimeout(() => {
            const scale = Math.max(2, window.devicePixelRatio || 2);

            domtoimage.toPng(container, {
                quality: 1.0,
                scale: scale,
                bgcolor: '#ffffff',
                width: container.scrollWidth,
                height: container.scrollHeight,
                style: {
                    transform: 'scale(1)',
                    transformOrigin: 'top left'
                }
            })
                .then((dataUrl) => {
                    const img = new Image();
                    img.onload = () => {
                        // Calculate optimal PDF dimensions
                        const maxWidth = 297; // A4 width in mm
                        const maxHeight = 210; // A4 height in mm

                        let pdfWidth = maxWidth;
                        let pdfHeight = (img.height * maxWidth) / img.width;

                        // If image is too tall, scale down proportionally
                        if (pdfHeight > maxHeight) {
                            pdfHeight = maxHeight;
                            pdfWidth = (img.width * maxHeight) / img.height;
                        }

                        // Create PDF with calculated dimensions
                        const pdf = new jsPDF({
                            orientation: pdfWidth > pdfHeight ? 'landscape' : 'portrait',
                            unit: 'mm',
                            format: [pdfWidth, pdfHeight]
                        });

                        // Add image to PDF
                        pdf.addImage(dataUrl, 'PNG', 0, 0, pdfWidth, pdfHeight);

                        // Generate filename from title
                        const filename = generateExportFilename(title, chartType, year, country);
                        pdf.save(`${filename}.pdf`);
                    };
                    img.onerror = () => {
                        console.error('Failed to load image for PDF export');
                        alert('Failed to generate PDF. Please try again.');
                    };
                    img.src = dataUrl;
                })
                .catch((err) => {
                    console.error('Export PDF error:', err);
                    alert('PDF export failed. Please try again or use a different browser.');
                });
        }, 1000); // Increased timeout for complex charts
    }, [title, chartData, chartType, year, property, country]);

    useEffect(() => {
        if (triggerExportPDF) {
            triggerExportPDF(handleExportPDF);
        }
    }, [triggerExportPDF, handleExportPDF]);

    return (
        <>
            {start ? (
                chartType === 'Bar' ? (
                    <BarChart
                        data={chartData}
                        year={year}
                        property={property}
                        setTitle={setTitle}
                        country={country}
                        hidden={false}
                    />
                ) : chartType === 'Pie' ? (
                    <PieChart
                        data={chartData}
                        property={property}
                        setTitle={setTitle}
                        year={year}
                        country={country}
                        hidden={false}
                        selectedTypology={selectedTypology}
                        onTypologyChange={setSelectedTypology}
                    />
                ) : chartType === 'Pile' ? (
                    <StackedBarChart
                        data={chartData}
                        setTitle={setTitle}
                        year={year}
                        country={country}
                        property={property}
                        hidden={false}
                        selectedTypology={selectedTypology}
                        onTypologyChange={setSelectedTypology}
                    />
                ) : (
                    <div>Invalid chart type</div>
                )
            ) : (
                <div id='loadingData'>No Data is available for the selected countries.</div>
            )}
        </>
    );
};

function HiddenChartContent({ data, chartType, year, property, country, setTitle, selectedTypology }) {
    if (!data || !data.length) return null;
    return (
        <div style={{ width: 'auto', height: 'auto', padding: 10, background: 'white' }}>
            {chartType === 'Bar' ? (
                <BarChart
                    data={data}
                    year={year}
                    property={property}
                    setTitle={setTitle}
                    country={country}
                    hidden={true}
                />
            ) : chartType === 'Pie' ? (
                <PieChart
                    data={data}
                    property={property}
                    setTitle={setTitle}
                    year={year}
                    country={country}
                    hidden={true}
                    selectedTypology={selectedTypology}
                />
            ) : chartType === 'Pile' ? (
                <StackedBarChart
                    data={data}
                    setTitle={setTitle}
                    year={year}
                    country={country}
                    property={property}
                    hidden={true}
                    selectedTypology={selectedTypology}
                />
            ) : (
                <div>Invalid chart type</div>
            )}
            <p className='exportText'>Source: World Geothermal Conference proceedings, Graphics: Manzella A. and Trumpy E. (CNR-IGG) Licence CC BY 4.0</p>
            <p className='exportText'>Statistics published in this interactive data tool are taken by published data. No use is made of confidential information obtained by the CNR-IGG.</p>
            <p className='exportText'>Check terminology as detailed in the VOG page (Technical Definitions).</p>
        </div>

    );
}

export default Chart;